import { useEffect, useMemo, useState } from "react";

import { useSearch } from "@/utils";
import { Order } from "@/types/types";
import { ListRelevanceMode } from "@/enum/list.enum";
import { useGetLeanListsQuery } from "@/pages/Private/redux/list/list.api";

import { List } from "../../../List/schema/list";
import { LIST_SCOPE_RELEVANCE_STATUSES, ListScope } from "./listScope";

import type { ListScopeFilterProps } from "./ListScopeFilter";

const LISTS_PER_PAGE = 15;

/** The props `useListScopeLists` owns; the page only adds its own selection handling. */
type OwnedFilterProps = Pick<
	ListScopeFilterProps,
	| "scope"
	| "lists"
	| "currentPage"
	| "totalPages"
	| "isFetching"
	| "isListLoading"
	| "handleSearch"
	| "handleFetchMore"
>;

interface ListScopeListsResult {
	/** Every list loaded so far — also used for the page's list-context header. */
	lists: List[];
	/** Spread onto `ListScopeFilter`; the page supplies `selectedLists` and `onApply`. */
	filterProps: OwnedFilterProps;
	/** Rewind pagination — call when the applied selection changes. */
	resetPage: () => void;
}

/**
 * Loads the lists for a messaging surface's scope filter: search, infinite-scroll pagination, and
 * the accumulated result set. Shared by Inbox, Outbox and Ready to send, which previously each kept
 * their own byte-identical copy of this block.
 *
 * Lists are requested in {@link ListRelevanceMode.RANK}: the surface's own lists sort first, but
 * lists whose leads currently live on another surface are still returned so the dropdown can show
 * and explain them instead of silently dropping them (PREC-3834).
 */
export const useListScopeLists = (scope: ListScope): ListScopeListsResult => {
	const [lists, setLists] = useState<List[]>([]);
	const [page, setPage] = useState<number>(1);
	const [isListLoading, setIsListLoading] = useState(false);

	const { setSearchValue, query, altered, setAltered } = useSearch();

	const { data, isFetching } = useGetLeanListsQuery({
		page,
		limit: LISTS_PER_PAGE,
		searchValue: query,
		sorting: { sortBy: "createdAt", sortOrder: Order.DESC },
		assignStatus: LIST_SCOPE_RELEVANCE_STATUSES[scope],
		relevanceMode: ListRelevanceMode.RANK,
	});

	useEffect(() => {
		if (!isFetching) {
			setAltered(false);
		}
	}, [isFetching]);

	// A new search replaces the accumulated set; paging appends to it.
	useEffect(() => {
		if (!data) {
			return;
		}

		if (altered) {
			setLists(data.data);
		} else {
			setLists((prev) => [
				...prev,
				...data.data.filter((list) => !prev.some((loaded) => loaded.id === list.id)),
			]);
		}

		setIsListLoading(false);
	}, [data, altered]);

	const totalPages = data?.meta.pageCount || 0;

	const filterProps = useMemo<OwnedFilterProps>(
		() => ({
			scope,
			lists,
			currentPage: page,
			totalPages,
			isFetching,
			isListLoading,
			handleSearch: (value: string) => {
				setIsListLoading(true);
				setSearchValue(value);
			},
			handleFetchMore: () => {
				if (!isFetching && page < totalPages) {
					setPage((current) => current + 1);
				}
			},
		}),
		[scope, lists, page, totalPages, isFetching, isListLoading]
	);

	return { lists, filterProps, resetPage: () => setPage(1) };
};
